import { canReadBankConnections } from '../../utils/financialAccess';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth';
import type { AccessPolicy as AccessPolicyShape } from '../../types/auth';
import { canUseAdminWorkspace, canUseMerchantPortal, resolveScopedMerchantLandingPath, workspaceHomePath } from '../../utils/accessPolicy';

type RequireAuthProps = {
  children: ReactNode;
  requiredPerms?: string[];
  requiredCapabilities?: (keyof AccessPolicyShape['capabilities'])[];
};

const matches = (userPerm: string, required: string) => {
  if (userPerm === required) return true;
  if (userPerm.endsWith('*')) {
    const prefix = userPerm.slice(0, -1);
    return required.startsWith(prefix);
  }
  return false;
};

export function RequireAuth({ children, requiredPerms, requiredCapabilities }: RequireAuthProps) {
  const { user, loading, policy } = useAuth();
  const location = useLocation();
  const pathname = location.pathname.replace(/\/+$/, '').toLowerCase();
  const workspace = policy?.workspace ?? 'admin';
  const rawPolicy = policy?.raw ?? null;

  if (loading) {
    return <div className="flex h-64 items-center justify-center text-slate-600">Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/staff/portal" replace state={{ from: location }} />;
  }

  // Workspace guard: merchant/party managers shouldn't access admin routes.
  if (pathname === '/admin' && !canUseAdminWorkspace(rawPolicy) && canUseMerchantPortal(rawPolicy)) {
    return <Navigate to={resolveScopedMerchantLandingPath(rawPolicy)} replace state={{ from: location }} />;
  }

  if (pathname.startsWith('/admin') && workspace !== 'admin' && !(pathname === '/admin/bank-connections' && canReadBankConnections(rawPolicy))) {
    return <Navigate to={workspaceHomePath(workspace)} replace />;
  }

  if (requiredPerms && requiredPerms.length) {
    const allowed = requiredPerms.some((perm) => user.perms.some((up) => matches(up, perm)));
    if (!allowed) {
      return <Navigate to="/merchant/portal" replace state={{ from: location }} />;
    }
  }
  if (requiredCapabilities && requiredCapabilities.length) {
    const allowed = requiredCapabilities.some((cap) => policy?.can(cap));
    if (!allowed) {
      return <Navigate to="/merchant/portal" replace state={{ from: location }} />;
    }
  }
  return <>{children}</>;
}
